import { formatDistanceToNow } from "date-fns";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  useMatches,
  useNavigate,
  useRouteLoaderData,
  useFetcher,
} from "react-router";
import type { RouteHandle } from "@/routes/config";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Globe, Lock, Pencil, Trash2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PlaygroundView } from "./PlaygroundView";
import { RobustnessView } from "./RobustnessView";

import { SpecificationsView } from "./SpecificationsView";
import {
  WORKSPACE_LOADER_ID,
  type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { RESULTS_LOADER_ID } from "@/routes/resultsLoader";
import { PLAYGROUND_LOADER_ID } from "@/routes/playgroundLoader";
import {
  SPECIFICATIONS_LOADER_ID,
  type SpecificationsLoaderData,
} from "@/routes/specificationsRoute";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";
import type { ResultsLoaderData } from "@/routes/resultsLoader";
import type { PlaygroundLoaderData } from "@/routes/playgroundLoader";
import {
  WORKSPACE_SIDEBAR_ACTION_PATH,
  type WorkspaceSidebarActionResult,
} from "@/routes/workspaceSidebarAction";

export interface Project {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  is_public: boolean;
  agent_id: string | null;
  code_id: string | null;
  behavior_dataset_id: string | null;
  behavior_dataset_selected_at: string | null;
  rules_dataset_id: string | null;
  rules_dataset_selected_at: string | null;
  owner_email: string;
}

interface ProjectViewProps {
  project: Project;
  userEmail?: string | undefined;
  onProjectUpdate?: (project: Project) => void;
}

type ProjectTab = "overview" | "specifications" | "playground" | "results";

const hasProjectTabHandle = (
  handle: unknown,
): handle is RouteHandle & { projectTab: ProjectTab } => {
  if (!handle || typeof handle !== "object") {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(handle, "projectTab");
};

export const ProjectView = ({
  project,
  userEmail,
  onProjectUpdate,
}: ProjectViewProps) => {
  const layoutData = useRouteLoaderData(
    WORKSPACE_LOADER_ID,
  ) as WorkspaceLoaderData;
  const playgroundLoaderData = useRouteLoaderData(PLAYGROUND_LOADER_ID) as
    | PlaygroundLoaderData
    | undefined;
  const resultsLoaderData = useRouteLoaderData(RESULTS_LOADER_ID) as
    | ResultsLoaderData
    | undefined;
  const specificationsLoaderData = useRouteLoaderData(
    SPECIFICATIONS_LOADER_ID,
  ) as SpecificationsLoaderData | undefined;
  const matches = useMatches();
  const navigate = useNavigate();
  const projectTabMatch = matches
    .slice()
    .reverse()
    .find(
      (match): match is typeof match & { handle: RouteHandle } =>
        hasProjectTabHandle(match.handle),
    );
  const activeTab = projectTabMatch?.handle.projectTab ?? "overview";
  const tabPaths = useMemo<Record<ProjectTab, string>>(
    () => ({
      overview: `/workspace/evals/${project.id}`,
      specifications: `/workspace/evals/${project.id}/specifications`,
      playground: `/workspace/evals/${project.id}/playground`,
      results: `/workspace/evals/${project.id}/results`,
    }),
    [project.id],
  );
  const handleTabChange = (nextTab: ProjectTab) => {
    if (nextTab === activeTab) return;
    navigate(tabPaths[nextTab]);
  };

  const codes = useMemo(
    () => layoutData.codes.map(({ id, title }) => ({ id, title })),
    [layoutData.codes],
  );
  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(
    project.code_id,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(project.title);
  const { toast } = useToast();
  const canEditProject =
    layoutData.userIsAdmin || layoutData.user.id === project.user_id;

  const {
    updateProjectTitle,
    toggleProjectPublic,
    assignConfiguration,
    deleteProject,
    isPending,
  } = useWorkspaceSidebarActions();

  const fetcher = useFetcher<WorkspaceSidebarActionResult>();
  const lastHandled = useRef<WorkspaceSidebarActionResult | null>(null);

  // Handle test run creation responses
  useEffect(() => {
    const data = fetcher.data;

    // Exit early if not idle or no data
    if (fetcher.state !== "idle" || !data) {
      return;
    }

    // Skip if we already handled this exact response object
    if (lastHandled.current === data) {
      return;
    }

    lastHandled.current = data;

    // Only handle testRunCreate intent
    if (data.intent === "testRunCreate") {
      if (data.ok) {
        toast({
          title: "Success",
          description: "Test run queued successfully",
        });
      } else if (data.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
      }
    }
  }, [fetcher.state, fetcher.data, toast]);

  // Playground action handlers
  const handleQueuePrimaryRun = (
    datasetId: string,
    evalType: string,
    judgeId: string | null,
    permittedValues?: string,
  ) => {
    if (!canEditProject) {
      toast({
        title: "Read-only",
        description: "You don't have permission to queue runs for this eval.",
        variant: "destructive",
      });
      return;
    }

    if (!datasetId) {
      toast({
        title: "Error",
        description: "No dataset selected",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "testRunCreate");
    formData.set("projectId", project.id);
    formData.set("testDataSetId", datasetId);
    formData.set("evalType", evalType);

    if (evalType === "LLM-As-A-Judge" && judgeId) {
      formData.set("judgeId", judgeId);
    }

    if (evalType === "Any permitted" && permittedValues) {
      formData.set("permittedValues", permittedValues);
    }

    fetcher.submit(formData, {
      method: "POST",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  const handleQueueDerivativeRun = (
    datasetId: string,
    evalType: string,
    judgeId: string | null,
    permittedValues?: string,
  ) => {
    if (!canEditProject) {
      toast({
        title: "Read-only",
        description: "You don't have permission to queue runs for this eval.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "testRunCreate");
    formData.set("projectId", project.id);
    formData.set("testDataSetId", datasetId);
    formData.set("evalType", evalType);

    if (evalType === "LLM-As-A-Judge" && judgeId) {
      formData.set("judgeId", judgeId);
    }

    if (evalType === "Any permitted" && permittedValues) {
      formData.set("permittedValues", permittedValues);
    }

    fetcher.submit(formData, {
      method: "POST",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  // Update selected agent/code when project changes
  useEffect(() => {
    setSelectedCodeId(project.code_id);
  }, [project.id, project.code_id]);

  const handleCodeChange = (codeId: string) => {
    if (!canEditProject) {
      return;
    }
    const nextCodeId = codeId === "none" ? null : codeId;
    setSelectedCodeId(nextCodeId);
    assignConfiguration(project.id, null, nextCodeId);
  };

  const handleTitleEdit = () => {
    if (!canEditProject) {
      return;
    }
    if (!editedTitle.trim() || editedTitle === project.title) {
      setIsEditingTitle(false);
      setEditedTitle(project.title);
      return;
    }

    const trimmed = editedTitle.trim();
    updateProjectTitle(project.id, trimmed);
    setIsEditingTitle(false);
    setEditedTitle(trimmed);
    onProjectUpdate?.({ ...project, title: trimmed });
  };

  const handleDelete = () => {
    if (!canEditProject) {
      return;
    }
    deleteProject(project.id);
    setDeleteDialogOpen(false);
  };

  return (
    <div className="p-8 w-full">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleEdit();
                    if (e.key === "Escape") {
                      setIsEditingTitle(false);
                      setEditedTitle(project.title);
                    }
                  }}
                  className="text-3xl font-bold h-12 w-96"
                  autoFocus
                  aria-label="Project title"
                  disabled={!canEditProject}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleTitleEdit}
                  className="h-10 w-10 p-0"
                  aria-label="Save project title"
                  disabled={!canEditProject || isPending("updateProjectTitle")}
                >
                  <Check className="h-5 w-5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditingTitle(false);
                    setEditedTitle(project.title);
                  }}
                  className="h-10 w-10 p-0"
                  aria-label="Cancel project title edit"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-foreground">
                  {project.title}
                </h1>
                {canEditProject ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(true)}
                    className="h-8 w-8 p-0"
                    aria-label="Edit project title"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                ) : null}
              </>
            )}
          </div>
          {canEditProject ? (
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          ) : null}
        </div>

        <p className="text-muted-foreground" data-testid="eval-overview-label">
          Eval Overview
        </p>

        <Tabs
          value={activeTab}
          onValueChange={(value) => handleTabChange(value as ProjectTab)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="specifications">Specifications</TabsTrigger>
            <TabsTrigger value="playground">Playground</TabsTrigger>
            <TabsTrigger value="results">Robustness</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Owner
                </h3>
                <p className="text-lg text-foreground">
                  {userEmail || "Unknown"}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Created
                </h3>
                <p className="text-lg text-foreground">
                  {formatDistanceToNow(new Date(project.created_at), {
                    addSuffix: true,
                  })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date(project.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Last Updated
                </h3>
                <p className="text-lg text-foreground">
                  {formatDistanceToNow(new Date(project.updated_at), {
                    addSuffix: true,
                  })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date(project.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Agent Configuration
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">
                    Select Agent
                  </label>
                  <Select
                    value={selectedCodeId || "none"}
                    onValueChange={handleCodeChange}
                    disabled={
                      !canEditProject || isPending("projectAssignConfiguration")
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select an agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No agent assigned</SelectItem>
                      {codes.map((code) => (
                        <SelectItem key={code.id} value={code.id}>
                          {code.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCodeId && (
                    <p className="text-xs text-muted-foreground mt-2">
                      This project will use the selected agent for test
                      execution
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Project Details
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Project ID:</span>
                  <span className="text-foreground font-mono">
                    {project.id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="text-foreground">Active</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Visibility:</span>
                    {project.is_public ? (
                      <Badge variant="default" className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        Public
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Private
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="project-public-switch"
                      className="text-sm cursor-pointer"
                    >
                      {project.is_public ? "Public" : "Private"}
                    </Label>
                    <Switch
                      id="project-public-switch"
                      checked={project.is_public}
                      onCheckedChange={(checked) =>
                        toggleProjectPublic(project.id, checked)
                      }
                      disabled={!canEditProject || isPending("projectTogglePublic")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="specifications" className="mt-6">
            {specificationsLoaderData ? (
              <SpecificationsView
                projectId={project.id}
                data={specificationsLoaderData}
                readOnly={!canEditProject}
              />
            ) : (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
                <p className="text-destructive font-medium">
                  Failed to load specifications
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  Please refresh the page to try again.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="playground" className="mt-6">
            {playgroundLoaderData ? (
              <PlaygroundView
                projectId={project.id}
                playgroundData={playgroundLoaderData}
                onQueuePrimaryRun={handleQueuePrimaryRun}
                onQueueDerivativeRun={handleQueueDerivativeRun}
                testRunCreationFetcher={fetcher}
                readOnly={!canEditProject}
              />
            ) : (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
                <p className="text-destructive font-medium">
                  Failed to load playground data
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  Please refresh the page to try again.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="results" className="mt-6">
            {resultsLoaderData ? (
              <RobustnessView
                projectId={project.id}
                data={resultsLoaderData}
                readOnly={!canEditProject}
              />
            ) : (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
                <p className="text-destructive font-medium">
                  Failed to load robustness data
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  Please refresh the page to try again.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{project.title}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};
